/**
 * Client typé pour les routes `/bag` — Phase 4, incrément 4.10.4,
 * étendu **offline-first** en Phase 3.3.3.
 *
 * Quatrième sub-entité coparenting. 4 méthodes : list/create/patch/
 * delete. Le PATCH est partiel — un seul champ ou plusieurs, le
 * serveur applique uniquement ce qui est fourni. Mutations passent
 * par `sendOrQueueMutation` (cf. 3.3.2).
 */
import { apiRequest } from './client.js';
import { sendOrQueueMutation, type SendResult } from './mutationsReplay.js';

export type ApiBagStatus = 'ok' | 'missing';

export interface ApiBagItem {
  id: string;
  homeId: string;
  item: string;
  category: string;
  location: string;
  status: ApiBagStatus;
  icon: string | null;
  requiredFor: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBagItemInput {
  item: string;
  category: string;
  location?: string;
  status?: ApiBagStatus;
  icon?: string;
  requiredFor?: string;
}

/**
 * Patch partiel — tous les champs optionnels. Au moins un doit
 * être fourni (sinon le serveur répond 400 `empty_patch`).
 */
export interface PatchBagItemInput {
  item?: string;
  category?: string;
  location?: string;
  status?: ApiBagStatus;
  icon?: string;
  requiredFor?: string;
}

export async function listBagItems(homeId: string): Promise<ApiBagItem[]> {
  const data = await apiRequest<{ bag: ApiBagItem[] }>(`/homes/${homeId}/bag`);
  return data.bag;
}

export async function createBagItem(
  homeId: string,
  input: CreateBagItemInput,
  localId?: string,
): Promise<SendResult<{ item: ApiBagItem }>> {
  return sendOrQueueMutation<{ item: ApiBagItem }>({
    method: 'POST',
    path: `/homes/${homeId}/bag`,
    body: input,
    ...(localId ? { localId } : {}),
  });
}

export async function patchBagItem(
  id: string,
  input: PatchBagItemInput,
): Promise<SendResult<{ item: ApiBagItem }>> {
  return sendOrQueueMutation<{ item: ApiBagItem }>({
    method: 'PATCH',
    path: `/bag/${id}`,
    body: input,
  });
}

export async function deleteBagItem(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/bag/${id}`,
  });
}
